<template>
  <Button
    :variant="variant"
    :size="size"
    :icon="action.icon"
    :disabled="action.enabled === false"
    :aria-label="action.tooltip || action.label"
    :title="action.tooltip"
    @click="handleAction"
    class="notification-action-button"
  >
    {{ action.label }}
  </Button>
</template>

<script setup lang="ts">
import Button from './Button.vue';
import type { NotificationAction } from '@/types/notifications';

export interface NotificationActionButtonProps {
  action: NotificationAction;
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium' | 'large';
}

const props = withDefaults(defineProps<NotificationActionButtonProps>(), {
  variant: 'secondary',
  size: 'small',
});

const emit = defineEmits<{
  action: [actionId: string];
}>();

const handleAction = async () => {
  if (props.action.enabled === false) return;
  
  try {
    await props.action.onClick();
    emit('action', props.action.id);
  } catch (error) {
    console.error('Notification action failed:', error);
  }
};
</script>

<style scoped lang="scss">
.notification-action-button {
  display: inline-flex;
  position: relative; // Create positioning context for dropdown
  z-index: 2; // Higher than notification content (z-index: 1) to establish proper stacking
  
  &__button {
    font-size: 12px;
    min-height: 26px;
    padding: 4px 8px;
    
    // Override button styles for notification context
    border-radius: var(--border-radius);
    background-color: var(--vscode-button-secondaryBackground);
    color: var(--vscode-button-secondaryForeground);
    border: 1px solid var(--vscode-button-border, transparent);
    
    &:hover:not(:disabled) {
      background-color: var(--vscode-button-secondaryHoverBackground);
    }
    
    &:focus {
      outline: 1px solid var(--vscode-focusBorder);
      outline-offset: 1px;
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    // Primary variant styling
    &.button--primary {
      background-color: var(--vscode-button-background);
      color: var(--vscode-button-foreground);
      
      &:hover:not(:disabled) {
        background-color: var(--vscode-button-hoverBackground);
      }
    }
  }
  
  &__dropdown {
    :deep(.dropdown-trigger) {
      width: 100%;
    }
  }
}
</style>
